/**
 * The Wald–Wolfowitz runs test for randomness of a two-valued sequence.
 *
 * A run is a maximal stretch of identical symbols. With n₁ symbols of one kind and n₂ of the
 * other (N = n₁ + n₂) and R runs, the null hypothesis is that every arrangement of the
 * symbols is equally likely. Too few runs means clustering (a trend or serial dependence);
 * too many means alternation.
 *
 * Exact distribution (Wald & Wolfowitz 1940), with C(a, b) a binomial coefficient:
 *
 *   P(R = 2k)     = 2 · C(n₁ − 1, k − 1) · C(n₂ − 1, k − 1) / C(N, n₁)
 *   P(R = 2k + 1) = [C(n₁ − 1, k) · C(n₂ − 1, k − 1) + C(n₁ − 1, k − 1) · C(n₂ − 1, k)] / C(N, n₁)
 *
 * Normal approximation: μ = 2n₁n₂/N + 1, σ² = 2n₁n₂(2n₁n₂ − N) / (N²(N − 1)), z = (R − μ)/σ
 * — the standard Wald–Wolfowitz z. The exact two-sided p-value is
 * min(1, 2 · min(P(R ≤ r), P(R ≥ r))). The classical tables stop at n₁, n₂ ≤ 20; beyond that
 * the normal approximation is accurate.
 *
 * Numbers are turned into two symbols by comparing with a cutoff (usually the median);
 * values equal to the cutoff have no side and are dropped (the usual convention).
 */

import { mean, median } from "@/lib/stats/descriptive";
import { normalCdf, normalUpper } from "@/lib/stats/distributions";
import { logChoose } from "@/lib/stats/special";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";

/** Largest group size for which the exact test is the headline p-value. */
export const EXACT_RUNS_LIMIT = 20;

export type CutoffRule = "median" | "mean" | "custom";

export interface RunsResult {
  /** 1 above the cutoff (or the first symbol), 0 below (or the second). */
  symbols: number[];
  n1: number;
  n2: number;
  n: number;
  runs: number;
  runLengths: number[];
  mean: number;
  variance: number;
  /** z for the observed runs, with the continuity correction when requested. */
  z: number;
  alternative: Alternative;
  pNormal: number;
  pExact: number;
  /** The p-value to report: exact when both groups are small, otherwise normal. */
  p: number;
  method: "exact" | "normal";
  /** Exact P(R ≤ r) and P(R ≥ r). */
  pExactLess: number;
  pExactGreater: number;
  pmfRuns: number[];
}

export type RunsOutcome =
  | { ok: true; result: RunsResult }
  | { ok: false; reason: "too-short" | "one-symbol" };

function choose(n: number, k: number): number {
  if (k < 0 || k > n || n < 0) {
    return 0;
  }
  return Math.exp(logChoose(n, k));
}

/** P(R = r) for every r from 0 to N (entries below 2 are zero). */
export function runsDistribution(n1: number, n2: number): number[] {
  const n = n1 + n2;
  const total = logChoose(n, n1);
  const pmf = new Array<number>(n + 1).fill(0);
  for (let r = 2; r <= n; r++) {
    const k = Math.floor(r / 2);
    let count: number;
    if (r % 2 === 0) {
      count = 2 * choose(n1 - 1, k - 1) * choose(n2 - 1, k - 1);
    } else {
      count =
        choose(n1 - 1, k) * choose(n2 - 1, k - 1) +
        choose(n1 - 1, k - 1) * choose(n2 - 1, k);
    }
    pmf[r] = count / Math.exp(total);
  }
  return pmf;
}

/** The run lengths of a sequence. */
export function runLengths(symbols: readonly number[]): number[] {
  const lengths: number[] = [];
  for (const [index, symbol] of symbols.entries()) {
    if (index > 0 && symbol === symbols[index - 1]) {
      lengths[lengths.length - 1] += 1;
    } else {
      lengths.push(1);
    }
  }
  return lengths;
}

/** Split numbers at a cutoff: 1 above, 0 below, and values equal to the cutoff dropped. */
export function dichotomize(
  values: readonly number[],
  cutoff: number
): { symbols: number[]; dropped: number } {
  const symbols: number[] = [];
  let dropped = 0;
  for (const value of values) {
    if (value > cutoff) {
      symbols.push(1);
    } else if (value < cutoff) {
      symbols.push(0);
    } else {
      dropped += 1;
    }
  }
  return { symbols, dropped };
}

/** The cutoff a rule picks for a set of values. */
export function cutoffFor(
  values: readonly number[],
  rule: CutoffRule,
  custom: number
): number {
  if (rule === "median") {
    return median(values);
  }
  return rule === "mean" ? mean(values) : custom;
}

export function runsTest(
  symbols: readonly number[],
  options: { alternative?: Alternative; continuity?: boolean } = {}
): RunsOutcome {
  const alternative = options.alternative ?? "two-sided";
  const n = symbols.length;
  if (n < 2) {
    return { ok: false, reason: "too-short" };
  }
  const n1 = symbols.filter((s) => s === 1).length;
  const n2 = n - n1;
  if (n1 === 0 || n2 === 0) {
    return { ok: false, reason: "one-symbol" };
  }
  const lengths = runLengths(symbols);
  const runs = lengths.length;
  const mu = (2 * n1 * n2) / n + 1;
  const variance = (2 * n1 * n2 * (2 * n1 * n2 - n)) / (n * n * (n - 1));
  let numerator = runs - mu;
  if (options.continuity && numerator !== 0) {
    numerator += numerator < 0 ? 0.5 : -0.5;
  }
  const z = variance > 0 ? numerator / Math.sqrt(variance) : 0;
  const pNormal = {
    "two-sided": Math.min(1, 2 * normalUpper(Math.abs(z))),
    less: normalCdf(z),
    greater: normalUpper(z),
  }[alternative];
  const pmf = runsDistribution(n1, n2);
  let pExactLess = 0;
  let pExactGreater = 0;
  for (let r = 0; r < pmf.length; r++) {
    if (r <= runs) {
      pExactLess += pmf[r];
    }
    if (r >= runs) {
      pExactGreater += pmf[r];
    }
  }
  pExactLess = Math.min(1, pExactLess);
  pExactGreater = Math.min(1, pExactGreater);
  const pExact = {
    "two-sided": Math.min(1, 2 * Math.min(pExactLess, pExactGreater)),
    less: pExactLess,
    greater: pExactGreater,
  }[alternative];
  const method =
    n1 <= EXACT_RUNS_LIMIT && n2 <= EXACT_RUNS_LIMIT ? "exact" : "normal";
  return {
    ok: true,
    result: {
      symbols: [...symbols],
      n1,
      n2,
      n,
      runs,
      runLengths: lengths,
      mean: mu,
      variance,
      z,
      alternative,
      pNormal,
      pExact,
      p: method === "exact" ? pExact : pNormal,
      method,
      pExactLess,
      pExactGreater,
      pmfRuns: pmf,
    },
  };
}
